/**
 * Company Search (R26) — the working surface, built to the sealed V3 design.
 *
 * State machine over the sealed states:
 *   1. first-visit  — the centered "Look up a company" hero (FirstVisitHero).
 *   2. results      — a resolved lookup is auto-selected and renders the full
 *                     company dashboard (CompanyProfile).
 *   3. unknown      — no resolved org → the external-scan state (UnknownToSpine).
 *   4. ambiguous    — candidates[] → the match picker (AmbiguousPicker).
 *   5. scan-pending — the honest pending confirmation inside the scan section.
 *   6. non-entitled — DEFERRED to Slice A (G7 entitles all active users today;
 *                     the sealed lock activates only against a real Contributor
 *                     entitlement, so nothing is built and no gate is invented).
 *
 * The page is auth-gated by the router's RequireAuth (matching the backend,
 * which admits any authenticated active user with no role gate); it adds no
 * client-side role gate of its own.
 */

import { useEffect, useState } from "react";
import { useCompanyLookup, useCompanyProfile } from "@/features/company-search/hooks";
import { SearchBar } from "@/features/company-search/components/SearchBar";
import { FirstVisitHero } from "@/features/company-search/components/FirstVisitHero";
import { LookupResults } from "@/features/company-search/components/LookupResults";
import { CompanyProfile } from "@/features/company-search/components/CompanyProfile";

interface Selection {
  organizationId: string;
  displayName: string | null;
  /** Resolver match_basis for the entity header; null for a manual pick. */
  matchBasis: string | null;
}

export function CompanySearch(): JSX.Element {
  const [committed, setCommitted] = useState("");
  const [selected, setSelected] = useState<Selection | null>(null);

  const lookup = useCompanyLookup(committed);
  const profile = useCompanyProfile(
    selected?.organizationId ?? null,
    selected?.displayName ?? null,
  );

  // A single resolved match renders the profile directly (sealed state 2), so
  // auto-select it. Only fires when nothing is selected yet, so re-searching a
  // resolved company after clearing works and there is no select/clear loop.
  const lookupView = lookup.data;
  useEffect(() => {
    if (lookupView?.kind === "resolved" && !selected) {
      setSelected({
        organizationId: lookupView.organizationId,
        displayName: lookupView.displayName,
        matchBasis: lookupView.matchBasis,
      });
    }
  }, [lookupView, selected]);

  const runSearch = (query: string) => {
    setSelected(null);
    setCommitted(query);
  };

  const clearSearch = () => {
    setSelected(null);
    setCommitted("");
  };

  // State 1: first visit — the sealed centered hero, not a bare search bar.
  if (committed.length === 0 && !selected) {
    return (
      <div className="sxco">
        <FirstVisitHero onSubmit={runSearch} />
      </div>
    );
  }

  return (
    <div className="sxco">
      <SearchBar onSubmit={runSearch} onClear={clearSearch} busy={lookup.isFetching} />

      {selected ? (
        <div className="sxco-profile-zone">
          {profile.isError ? (
            <p className="sxco-error">
              {profile.error instanceof Error ? profile.error.message : "Failed to load profile."}
            </p>
          ) : profile.isPending ? (
            <p className="sxco-muted">Loading company profile…</p>
          ) : profile.data === null ? (
            <div className="sx-empty">
              <h2>No profile on record</h2>
              <p>This company did not resolve to a delivery profile.</p>
            </div>
          ) : profile.data ? (
            // Key on the org so the profile subtree (incl. its scan state)
            // remounts per company; without it, returning to a cached company
            // re-renders in place and would show a prior company's scan state.
            <CompanyProfile
              key={selected.organizationId}
              profile={profile.data}
              matchBasis={selected.matchBasis}
            />
          ) : null}
        </div>
      ) : (
        <div className="sxco-results-zone">
          {lookup.isError ? (
            <div className="sx-empty">
              <h2>Company lookup failed</h2>
              <p>{lookup.error instanceof Error ? lookup.error.message : "Please try again."}</p>
            </div>
          ) : lookup.data ? (
            <LookupResults
              view={lookup.data}
              query={committed}
              selectedId={null}
              onSelect={(organizationId, displayName) =>
                setSelected({ organizationId, displayName, matchBasis: null })
              }
              onRefine={clearSearch}
            />
          ) : (
            <p className="sxco-muted">Searching…</p>
          )}
        </div>
      )}
    </div>
  );
}
